import React, { createContext, useCallback, useContext, useRef, useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

/**
 * Drop-in replacement for window.confirm() that uses shadcn AlertDialog
 * styled to the Weave Workbook design language. Returns a Promise<boolean>.
 *
 * Usage:
 *   const confirm = useConfirm();
 *   const ok = await confirm({
 *     title: "Release this slot?",
 *     description: "Other teams will see it become available again.",
 *     confirmText: "Release",
 *     destructive: true,
 *   });
 *   if (!ok) return;
 */

const ConfirmContext = createContext(null);

const DEFAULTS = {
  title: "Are you sure?",
  description: "",
  confirmText: "Confirm",
  cancelText: "Cancel",
  destructive: false,
};

export function ConfirmProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [opts, setOpts] = useState(DEFAULTS);
  const resolverRef = useRef(null);

  const confirm = useCallback((options = {}) => {
    setOpts({ ...DEFAULTS, ...options });
    setOpen(true);
    return new Promise((resolve) => {
      resolverRef.current = resolve;
    });
  }, []);

  const close = (result) => {
    setOpen(false);
    if (resolverRef.current) {
      const r = resolverRef.current;
      resolverRef.current = null;
      // Defer slightly so the dialog finishes its close animation before
      // any follow-up state changes from the caller.
      setTimeout(() => r(result), 0);
    }
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <AlertDialog open={open} onOpenChange={(o) => !o && close(false)}>
        <AlertDialogContent
          className="rounded-none border-2 border-[#0A0A0A] max-w-md"
          data-testid="confirm-dialog"
        >
          <AlertDialogHeader>
            <AlertDialogTitle
              className="font-display text-2xl font-black tracking-tighter"
              data-testid="confirm-dialog-title"
            >
              {opts.title}
            </AlertDialogTitle>
            <AlertDialogDescription
              className={
                opts.description
                  ? "text-sm text-[#525252] whitespace-pre-wrap leading-relaxed"
                  : "sr-only"
              }
              data-testid="confirm-dialog-description"
            >
              {opts.description || "Please confirm or cancel this action."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => close(false)}
              className="rounded-none border-[#0A0A0A]"
              data-testid="confirm-dialog-cancel"
            >
              {opts.cancelText}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => close(true)}
              className={`rounded-none ${
                opts.destructive
                  ? "bg-[#A23232] hover:bg-[#8a2828] text-white"
                  : "bg-[#2596be] hover:bg-[#1e7da3] text-white"
              }`}
              data-testid="confirm-dialog-confirm"
            >
              {opts.confirmText}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    // Safe fallback so a component used outside the provider still works.
    return (opts = {}) =>
      Promise.resolve(
        window.confirm(opts.title || "Are you sure?") // eslint-disable-line no-alert
      );
  }
  return ctx;
}
